import { buildPageTitle, useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useClientId } from "@/shared/hooks/useClientId";

import { PropertyTabExportMenu } from "@/features/exports/components/PropertyTabExportMenu";
import { useExportsEnabled } from "@/shared/hooks/useExportsEnabled";
import { supabase } from "@/shared/integrations/supabase/client";
import { usePropertyOverviewData } from "@/features/properties/hooks/usePropertyOverviewData";
import { getAssetsUnder10YearsPercentage } from "@/features/assets/services/assetsQueries";
import { Gauge, Ruler, Thermometer, Wind, Zap } from "lucide-react";

import { PropertyDetailContent } from "./clientPropertyDetail/PropertyDetailContent";
import { PropertyDetailHeader } from "./clientPropertyDetail/PropertyDetailHeader";
import { PropertyDetailLoadingState } from "./clientPropertyDetail/PropertyDetailLoadingState";
import { PropertyDetailTabsNav } from "./clientPropertyDetail/PropertyDetailTabsNav";
import { PropertyNotFoundState } from "./clientPropertyDetail/PropertyNotFoundState";
import {
  getPropertyTabFromParam,
  type Property,
  type PropertyDetailFields,
  type PropertyTab,
} from "./clientPropertyDetail/types";

export default function ClientPropertyDetail () {
  const { propertyId: routeParam } = useParams<{ propertyId: string }>();
  // The route param is either a Firebase project id (ref_id) — public
  // /facility/{projectID} links — or our Supabase property id (legacy
  // /facility/property/{id} links). Resolve it to the Supabase id used
  // throughout the rest of this component.
  const { data: propertyId, isLoading: resolvingPropertyId } = useQuery({
    queryKey: ["resolve-property-id", routeParam],
    queryFn: async () => {
      if (!routeParam) return null;
      const UUID_RE =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const column = UUID_RE.test(routeParam) ? "id" : "ref_id";
      const { data, error } = await supabase
        .from("properties")
        .select("id")
        .eq(column, routeParam)
        .maybeSingle();
      if (error) throw error;
      return (data?.id as string | undefined) ?? null;
    },
    enabled: !!routeParam,
  });
  const { clientId } = useClientId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, loading } = useProjects();

  const tabParam = searchParams.get("tab");
  const activeTab = getPropertyTabFromParam(tabParam);
  const [propertySearchTerm, setPropertySearchTerm] = useState("");
  const { enabled: exportsEnabled, loading: exportsLoading } = useExportsEnabled(clientId);

  const updateActiveTab = useCallback((nextTab: PropertyTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === "overview") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", nextTab);
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!exportsLoading && activeTab === "exports" && !exportsEnabled) {
      updateActiveTab("overview");
    }
  }, [activeTab, exportsEnabled, exportsLoading, updateActiveTab]);

  useEffect(() => {
    if (tabParam === "report") {
      updateActiveTab("photos");
    } else if (tabParam === "3d") {
      updateActiveTab("files-documents");
    }
  }, [tabParam, updateActiveTab]);

  // Fetch asset count for this property (same logic as PropertyDetailDrawer)
  const { data: assetCount } = useQuery({
    queryKey: ["property-asset-count", propertyId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("property_assets")
        .select("*", { count: "exact", head: true })
        .eq("property_id", propertyId!);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!propertyId,
  });

  // Compliance score: percentage of assets under 10 years old
  const { data: complianceScore } = useQuery({
    queryKey: ["property-compliance-under10", propertyId],
    queryFn: () => getAssetsUnder10YearsPercentage(propertyId!),
    enabled: !!propertyId,
  });

  // Latest survey date for this property — same source the properties list uses
  // (most recent property_attributes.captured_at).
  const { data: surveyDate } = useQuery({
    queryKey: ["property-survey-date", propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("property_attributes")
        .select("captured_at")
        .eq("property_id", propertyId!)
        .order("captured_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data?.captured_at as string | undefined) ?? null;
    },
    enabled: !!propertyId,
  });

  // Get all properties for this client
  const allProperties = useMemo(() => {
    const project = projects.find(p => p.id === clientId);
    if (!project) return [];

    return project.properties.map(p => ({
      id: p.id,
      name: p.name,
      address: `${p.address || ''} ${p.city || ''} ${p.state || ''}`.trim(),
      type: p.property_type || 'Commercial',
    }));
  }, [projects, clientId]);

  // Filter properties based on search term
  const filteredProperties = useMemo(() => {
    if (!propertySearchTerm.trim()) return allProperties;

    const searchLower = propertySearchTerm.toLowerCase().trim();
    return allProperties.filter(prop =>
      prop.name.toLowerCase().includes(searchLower) ||
      prop.address.toLowerCase().includes(searchLower)
    );
  }, [allProperties, propertySearchTerm]);

  // Get client/project data
  const client = useMemo(() => {
    return projects.find(p => p.id === clientId);
  }, [projects, clientId]);

  // Get property data
  const property = useMemo(() => {
    const project = projects.find(p => p.id === clientId);
    if (!project) return null;

    const propertyData = project.properties.find(p => p.id === propertyId);
    if (!propertyData) return null;
    const propertyDetails = propertyData as typeof propertyData & PropertyDetailFields;

    return {
      id: propertyData.id,
      name: propertyData.name,
      address: `${propertyData.address || ''} ${propertyData.city || ''} ${propertyData.state || ''}`.trim(),
      type: propertyData.property_type || 'Commercial',
      status: propertyDetails.project_status || 'Active',
      asset_count: assetCount ?? 0,
      survey_date: surveyDate
        ? new Date(surveyDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
        : null,
      compliance_score: complianceScore ?? 0,
      facility_image_url: propertyDetails.facility_image_url,
    } as Property;
  }, [projects, clientId, propertyId, assetCount, complianceScore, surveyDate]);

  useDocumentTitle(
    client && property
      ? buildPageTitle([property.name, client.name], "| Property Detail")
      : null
  );

  const { data: kpi } = usePropertyOverviewData(propertyId ?? undefined, clientId ?? undefined);
  const headerSpecs = kpi
    ? [
      { icon: Ruler, label: "Sq Ft", value: kpi.squareFootage != null ? kpi.squareFootage.toLocaleString() : null },
      { icon: Thermometer, label: "Climate Zone", value: kpi.climateZone ? kpi.climateZone.replace(/\s*\(.*\)\s*$/, "").trim() : null },
      { icon: Wind, label: "HVAC Captured", value: kpi.hvacTonnageInstalled != null ? `${Math.round(kpi.hvacTonnageInstalled)}T` : null },
      { icon: Gauge, label: "HVAC Required", value: kpi.minTonnageRequired != null ? `${Math.round(kpi.minTonnageRequired)}T` : null },
      { icon: Zap, label: "Electrical", value: kpi.electricalPanel },
    ].filter((s) => s.value)
    : [];

  const handlePropertySwitch = (newPropertyId: string) => {
    navigate(`/property/${newPropertyId}`);
    setPropertySearchTerm("");
  };

  const tabs: Array<{ value: PropertyTab; label: string }> = [
    { value: 'overview', label: 'Overview' },
    { value: 'assets', label: 'Assets' },
    { value: 'site-attributes', label: 'Site Attributes' },
    { value: 'deficiencies', label: 'Deficiencies' },
    { value: 'photos', label: 'Photos' },
    { value: 'vr', label: 'VR Tours' },
    { value: 'files-documents', label: 'Files & Documents' },
    ...(exportsEnabled ? [{ value: 'exports' as const, label: 'Import & Export' }] : []),
  ];

  // Loading state (include ref_id → id resolution so we don't flash "not found")
  if (loading || resolvingPropertyId) {
    return <PropertyDetailLoadingState />;
  }

  // Not found state
  if (!client || !property) {
    return <PropertyNotFoundState onBack={() => navigate("/properties")} />;
  }

  const showInlineExportMenu =
    !!clientId
    && !!propertyId
    && exportsEnabled
    && activeTab !== 'overview'
    && activeTab !== 'site-attributes'
    && activeTab !== 'assets'
    && activeTab !== 'photos'
    && activeTab !== 'files-documents'
    && activeTab !== 'vr'
    && activeTab !== 'deficiencies'
    && activeTab !== 'exports';

  return (
    <div className="h-screen bg-gradient-to-br from-background via-background to-accent/20 flex flex-col">
      {/* Header - Matching Drawer Design */}
      <div className="bg-gradient-to-r from-card/80 to-accent/80 backdrop-blur-sm border-b border-border flex-shrink-0">
        <div className="p-6">
          <PropertyDetailHeader
            property={property}
            clientLogoUrl={client?.logo_url}
            clientName={client?.name}
            filteredProperties={filteredProperties}
            searchTerm={propertySearchTerm}
            onSearchTermChange={setPropertySearchTerm}
            onPropertySwitch={handlePropertySwitch}
            onBackToClient={() => navigate("/properties")}
            headerSpecs={headerSpecs}
          />

          <PropertyDetailTabsNav
            activeTab={activeTab}
            tabs={tabs}
            onTabChange={updateActiveTab}
          />
        </div>
      </div>

      {/* Inline export menu — gated by per-project feature flag. Hidden on
          tabs that render their own inline Export or don't need one
          (Overview cards, Site Attributes, Site Report(s), Photos,
          Files & Documents, VR Tours, Deficiencies). */}
      {showInlineExportMenu && (
        <div className="flex justify-end px-6 pt-4">
          <PropertyTabExportMenu
            activeTab={activeTab}
            clientId={clientId!}
            propertyId={propertyId!}
            propertyLabel={property?.name || "Property"}
            onJumpToExportsTab={() => updateActiveTab('exports')}
          />
        </div>
      )}

      {/* Content */}
      <PropertyDetailContent
        activeTab={activeTab}
        clientId={clientId!}
        propertyId={propertyId!}
        property={property}
        exportsEnabled={exportsEnabled && !!clientId && !!propertyId}
        onJumpToExports={() => updateActiveTab('exports')}
      />
    </div>
  );
}
